import { regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios from 'axios';
import i18next from 'i18next';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReCAPTCHA from 'react-google-recaptcha';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { changeLang } from '../../redux/actions/appSettings.actions';
import { openSnackBar } from '../../redux/actions/snackbar.actions';
import { getApiUrl } from '../../utils/apiUrls';
import { apiErrorHandler } from '../../utils/errorHandler';
import { checkPassword } from '../../utils/helperFn';
import CustomToolTip from '../custom-tooltip/CustomTooltip.component';

const initial_state = {
    email: {
        value: '',
        isRequired: true,
        msg: null
    },
    password: {
        value: '',
        isRequired: true,
        msg: null
    },
    passwordVisible: false,
    passwordConfirm: {
        value: '',
        isRequired: true,
        msg: null
    },
    passwordConfirmVisible: false,
    validOptions: {
        requiredFields: ['email', 'password', 'passwordConfirm'],
        errorFields: [],
    },
};

const SetPasswordForm = ({ t, decryptId }) => {
    const [isLoading, setIsLoading] = React.useState(false);
    const [formVal, setFormVal] = React.useState(initial_state);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const env = useSelector(state => state?.env);
    const recaptchaRef = React.useRef(null);
    const captchaWidget = React.useMemo(() => {
        if (env?.RECAPTCHA?.ENABLE_RECAPTCHA) {
            return (
                <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={env?.RECAPTCHA?.SITE_KEY}
                    size="invisible"
                    className="cm-recaptcha"
                />
            );
        }
        return null;
    }, [env?.RECAPTCHA?.ENABLE_RECAPTCHA]);

    const [searchParams] = useSearchParams();
    const getLang = searchParams.get('lang');
    const getRefId = searchParams.get('refId');

    React.useEffect(() => {
        if(!!getLang && !!i18next?.isInitialized) {
            setTimeout(() => {
                i18next.changeLanguage(getLang);
                // i18next.reloadResources([getLang]);
                dispatch(changeLang(getLang))
            }, 500)
        }
    }, [i18next]);

    const handleFieldChange = (e, fieldName) => {
        setFormVal({
            ...formVal,
            [fieldName]: {
                ...formVal[fieldName],
                value: e.target.value,
            }
        })
    }

    const handlePassVisiblity = (fieldName) => {
        setFormVal({
            ...formVal,
            [fieldName]: !formVal[fieldName]
        })
    }

    React.useEffect(() => {
        !!decryptId && getEmailId();

        // eslint-disable-next-line
    }, [decryptId])

    const triggerCaptchaAndSetPassword = async () => {
        if (env?.RECAPTCHA?.ENABLE_RECAPTCHA) {
            const token = await recaptchaRef.current.executeAsync();
            recaptchaRef.current?.reset();
            if (!token) {
                dispatch(openSnackBar({ msg: t('captcha_verification_failed'), type: 'error' }));
                setIsLoading(false);
                return;
            }
            encProcess(token);
        } else {
            encProcess(null);
        }
    };
    
    const getEmailId = async () => {
        try {
            setIsLoading(true);
            const res = await axios.get(`${getApiUrl('decryptId')}?id=${decryptId}&action=reset`);
    
            if(!!res.data) {
                setIsLoading(false);
                setFormVal({
                    ...formVal,
                    email: {
                        ...formVal['email'],
                        value: res.data,
                    }
                })
            }
        } catch (err) {
            setIsLoading(false);
            const errObj = apiErrorHandler(err);
            if(errObj.statusText === "password_reset_link_expired" ){
              navigate("/login");
            }

            setTimeout(() => {
                dispatch(openSnackBar({
                    msg: t(errObj.statusText),
                    type: 'error'
                }))
            }, 1000);
        }
    }

    const setPassword = async (data) => {
        try {
            setIsLoading(true);
            const res = await axios.post(`${getApiUrl('resetPassword')}?id=${searchParams.get('id')}`, data); 
    
            if(res.data.status === "SUCCESS") {
                setIsLoading(false);
                dispatch(openSnackBar({
                    msg: t(res.data.message),
                    type: 'success'
                }));

                navigate(`/login${!!getLang ? `?lang=${getLang}${getRefId ? `${getLang ? '&' : '?'}refId=${getRefId}` : ''}` : ''}`);
            } else {
                throw Error(res.data);
            }
        } catch (err) {
            setIsLoading(false);
            const errObj = apiErrorHandler(err);

            dispatch(openSnackBar({
                msg: t(errObj.statusText),
                type: 'error'
            }))
        }
    }

    const getReqMsg = (fieldName) => {
        if(fieldName === 'email') {
            return t('email_required');
        } else if(fieldName === 'password' || fieldName === 'passwordConfirm') {
            return t('new_password_required');
        } else {
            return t('field_required')
        }
    }

    const handleValidation = () => {
        //Check required fields are filled
        let currentFormVal = JSON.parse(JSON.stringify(formVal));

        formVal.validOptions.requiredFields.forEach(el => {
            if(currentFormVal[el].value === '') {
                currentFormVal[el].msg = getReqMsg(el);
            } else {
                //Email Validation
                if(el === 'email') {
                    // eslint-disable-next-line
                    const regex = /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
                    currentFormVal.email.msg = !regex.test(currentFormVal.email.value) ? t("valid_email_address"): null;
                    return;         
                }

                //Password Validation
                if(el === 'password') {
                    const isValid = checkPassword(currentFormVal.password.value);
                    currentFormVal.password.msg = !isValid ? t("valid_password") : null;
                    return; 
                }
                if(el === 'passwordConfirm') {
                    const isValid = checkPassword(currentFormVal.passwordConfirm.value);
                    currentFormVal.passwordConfirm.msg = !isValid ? t("valid_password") : null;
                }

                //Match Passwords
                if(el === 'passwordConfirm') {
                    currentFormVal.passwordConfirm.msg = currentFormVal.password.value !== currentFormVal.passwordConfirm.value ? t('passwords_not_match') : null;
                    console.log("currentFormVal.passwordConfirm.msg", currentFormVal.password.value, currentFormVal.passwordConfirm.value, currentFormVal.passwordConfirm.msg);
                    return;
                }

                currentFormVal[el].msg = null;
            }
        });

        //Check if form is valid
        let isValidFlag = 0;

        currentFormVal.validOptions.requiredFields.forEach((el) => {
            if(currentFormVal[el].msg === null) {
                isValidFlag++;
            }
        });

        if(isValidFlag === currentFormVal.validOptions.requiredFields.length) {
            //Api Call
            if (!isLoading) {
                setIsLoading(true);
                triggerCaptchaAndSetPassword();
            }
        } else {
            setFormVal({
                ...formVal,
                ...currentFormVal,
            })
        }

    }


    const encProcess = async (recaptchaToken) => {
        try {
            const getKey = await axios.get(getApiUrl('getSk'));
            if(!!getKey.data) {
                setIsLoading(false);
                const JSEncrypt = window.JSEncrypt;

                //RSA Public Key
                const rsaKey = getKey.data.key;
                const passToEncrypt = formVal.password.value;
                const confirmPassToEncrypt = formVal.passwordConfirm.value;
                const skid = getKey.data.id;

                //encrypt AES key with RSA public key
                var rsaEncrypt = new JSEncrypt({default_key_size: 2048});
                rsaEncrypt.setPublicKey(rsaKey);
                var rsaEncryptedAesKey = rsaEncrypt.encrypt(passToEncrypt.toString());
                var rsaEncryptedAesKey2 = rsaEncrypt.encrypt(confirmPassToEncrypt.toString());

                var encryptedTransaction = { 
                    skid,
                    email: formVal.email.value,
                    password: rsaEncryptedAesKey.toString(), 
                    confirmPassword: rsaEncryptedAesKey2.toString(),
                    recaptchaToken
                };

                setPassword(encryptedTransaction);
            }

        } catch (error) {
            setIsLoading(false);
            const errObj = apiErrorHandler(error);

            dispatch(openSnackBar({
                msg: t(errObj.statusText),
                type: 'error'
            }))
        }
    }

    const handleFormSubmit = (e) => {
        e.preventDefault();
        handleValidation();
    }

    return(
        <form onSubmit={handleFormSubmit} className="cm-auth-form cm-login-form">
            <div className="form-group mb-3">
                <label htmlFor="email" className='pb-2 text-blue-gray-700 cm-xs-txt fw-medium'>{t("login_email_label")}</label>
                <input type="email" className="form-control text-blue-800 fw-medium" id="email" value={formVal.email.value} placeholder={t("login_email_placeholder")} onChange={(e) => handleFieldChange(e, 'email')} readOnly={true} />
                {formVal.email.msg !== null && <span className='cm-xs-txt text-danger fw-medium pt-2'>{formVal.email.msg}</span>}
            </div>
            <div className="form-group mb-3">
                <label htmlFor="password" className='pb-2 text-blue-gray-700 cm-xs-txt fw-medium'>{t("set_a_password")}</label>
                <div className="cm-icon-field position-relative cm-double-icon-field">
                    {formVal.passwordVisible ? <input type={'text'} autocomplete="true" className="form-control text-blue-800 fw-medium" id="password" value={formVal.password.value} placeholder={t("password")} onChange={(e) => handleFieldChange(e, 'password')} />:<input type={'password'} autocomplete="true" className="form-control text-blue-800 fw-medium" id="password" value={formVal.password.value} placeholder={t("password")} onChange={(e) => handleFieldChange(e, 'password')} />}
                    <FontAwesomeIcon icon={formVal.passwordVisible ? regular('eye-slash') : regular('eye')}  className={`form-text text-blue-gray-500 position-absolute cm-pass-visiblility-changer`} onClick={() => handlePassVisiblity('passwordVisible')} />
                    <CustomToolTip 
                        title={
                            <>
                                <span className='my-0 cm-sm-txt fw-medium'>{t("your_password_should_be")}</span>
                                <ul className='cm-tooltip-pass-info mt-3 mb-0 ps-3 cm-sm-txt fw-medium'>
                                    <li>{t("pass_point_1")}</li>
                                    <li>{t("pass_point_2")}</li>
                                    <li>{t("pass_point_3")} <span style={{ display: "block"}}>{t("password_allowed_special_characters")}</span></li>
                                    <li>{t("pass_point_4")}</li>
                                </ul>
                            </>
                        }
                    >
                        <FontAwesomeIcon icon={regular("question-circle")} className={`form-text text-blue-gray-500 position-absolute cm-pass-info-icon`} />
                    </CustomToolTip>
                </div>
                {formVal.password.msg !== null && <span className='cm-xs-txt text-danger fw-medium pt-2'>{formVal.password.msg}</span>}
            </div>
            <div className="form-group mb-3">
                <label htmlFor="password" className='pb-2 text-blue-gray-700 cm-xs-txt fw-medium'>{t("confirm_password")}</label>
                <div className="cm-icon-field position-relative cm-double-icon-field">
                    {formVal.passwordConfirmVisible ? <input type={'text'} autocomplete="true" className="form-control text-blue-800 fw-medium" id="password-confirm" value={formVal.passwordConfirm.value} placeholder={t("password")} onChange={(e) => handleFieldChange(e, 'passwordConfirm')} />:<input type={'password'} autocomplete="true" className="form-control text-blue-800 fw-medium" id="password-confirm" value={formVal.passwordConfirm.value} placeholder={t("password")} onChange={(e) => handleFieldChange(e, 'passwordConfirm')} />}
                    <FontAwesomeIcon icon={formVal.passwordConfirmVisible ? regular('eye-slash') : regular('eye')}  className={`form-text text-blue-gray-500 position-absolute cm-pass-visiblility-changer`} onClick={() => handlePassVisiblity('passwordConfirmVisible')} />
                    <CustomToolTip 
                        title={
                            <>
                                <span className='my-0 cm-sm-txt fw-medium'>{t("your_password_should_be")}</span>
                                <ul className='cm-tooltip-pass-info mt-3 mb-0 ps-3 cm-sm-txt fw-medium'>
                                    <li>{t("pass_point_1")}</li>
                                    <li>{t("pass_point_2")}</li>
                                    <li>{t("pass_point_3")} <span style={{ display: "block"}}>{t("password_allowed_special_characters")}</span></li>
                                    <li>{t("pass_point_4")}</li>
                                </ul>
                            </>
                        }
                    >
                        <FontAwesomeIcon icon={regular("question-circle")} className={`form-text text-blue-gray-500 position-absolute cm-pass-info-icon`} />
                    </CustomToolTip>
                </div>
                {formVal.passwordConfirm.msg !== null && <span className='cm-xs-txt text-danger fw-medium pt-2'>{formVal.passwordConfirm.msg}</span>}
            </div>

            {captchaWidget}
            <div className="cm-form-btn-group mt-4 pt-2 d-flex align-items-center">
                <button onClick={handleFormSubmit} className={`cm-icon-btn position-relative btn btn-primary cm-btn cm-prim-btn d-flex align-items-center cm-sm-txt ${isLoading ? 'cm-disable-btn' : ''}`}>
                    <span className="text-white">{t("set_up_new_password")}</span>
                    <FontAwesomeIcon icon={regular('angle-right')} className="ps-2" />
                </button>
            </div>
        </form>
    )

}

export default SetPasswordForm;